/**
 * Org-unit tree for party owner pickers.
 * Reference rows carry `parent`; depth is derived so a missing `level` still nests.
 */

const EMPTY = Object.freeze([]);

/**
 * @param {object | null | undefined} unit
 * @returns {string | null}
 */
export function unitParentId(unit) {
    if (!unit || typeof unit !== 'object') return null;
    const parent = unit.parent ?? unit.parentId ?? null;
    if (parent == null || parent === '') return null;
    if (typeof parent === 'object') {
        const id = parent._id || parent.id || '';
        return id ? String(id) : null;
    }
    return String(parent);
}

function sortByLabel(nodes) {
    nodes.sort((a, b) => a.label.localeCompare(b.label, 'tr', { sensitivity: 'base' }));
    return nodes;
}

function linkedChildren(node) {
    return node.children.filter((child) => child.ancestorIds.at(-1) === node.id);
}

function collectDescendantIds(node) {
    const ids = [];
    const walk = (current) => {
        for (const child of linkedChildren(current)) {
            ids.push(child.id);
            walk(child);
        }
    };
    walk(node);
    return ids;
}

/**
 * @param {Array<object>} rawUnits entity rows or picker options
 * @returns {{ ordered: object[], byId: Map<string, object> }}
 */
export function indexUnitOptions(rawUnits) {
    const byId = new Map();
    for (const unit of Array.isArray(rawUnits) ? rawUnits : EMPTY) {
        const id = String(unit?.value ?? unit?._id ?? unit?.id ?? '').trim();
        if (!id || byId.has(id)) continue;
        const label = String(unit?.label || unit?.name || unit?.code || id);
        byId.set(id, {
            id,
            label,
            code: unit?.code ? String(unit.code) : '',
            status: unit?.status ? String(unit.status) : '',
            parentId: unitParentId(unit),
            children: [],
            depth: 0,
            ancestorIds: [],
            descendantIds: [],
            hasChildren: false,
            pathLabel: label,
        });
    }

    const roots = [];
    for (const node of byId.values()) {
        const parent = node.parentId ? byId.get(node.parentId) : null;
        if (!parent || parent.id === node.id) {
            node.parentId = null;
            roots.push(node);
            continue;
        }
        parent.children.push(node);
    }
    sortByLabel(roots);

    const ordered = [];
    const visited = new Set();
    const visit = (node, depth, ancestors) => {
        if (visited.has(node.id)) return;
        visited.add(node.id);
        node.depth = depth;
        node.ancestorIds = ancestors;
        const ancestorLabels = ancestors.map((ancestorId) => byId.get(ancestorId)?.label).filter(Boolean);
        node.pathLabel = [...ancestorLabels, node.label].join(' / ');
        sortByLabel(node.children);
        ordered.push(node);
        for (const child of node.children) {
            visit(child, depth + 1, [...ancestors, node.id]);
        }
    };

    for (const root of roots) visit(root, 0, []);
    for (const node of byId.values()) {
        if (!visited.has(node.id)) visit(node, 0, []);
    }
    for (const node of byId.values()) {
        node.hasChildren = linkedChildren(node).length > 0;
        node.descendantIds = collectDescendantIds(node);
    }

    return { ordered, byId };
}

/**
 * Deepest root-relative depth in the tree. 0 means every unit is a root.
 * @param {Array<{ depth?: number }>} ordered
 */
export function treeDepthSpan(ordered) {
    let max = 0;
    for (const node of ordered || EMPTY) {
        const depth = Number(node?.depth) || 0;
        if (depth > max) max = depth;
    }
    return max;
}

/**
 * Parents expanded on first open. Small trees stay open; large trees show the first level.
 * @param {Array<{ id: string, hasChildren?: boolean, depth?: number }>} ordered
 * @param {{ expandAllBelow?: number }} [opts]
 * @returns {Set<string>}
 */
export function defaultExpandedUnitIds(ordered, opts = {}) {
    const limit = Number.isFinite(opts.expandAllBelow) ? opts.expandAllBelow : 40;
    const ids = new Set();
    const total = Array.isArray(ordered) ? ordered.length : 0;
    for (const node of ordered || EMPTY) {
        if (!node?.hasChildren) continue;
        if (total <= limit || node.depth === 0) ids.add(node.id);
    }
    return ids;
}

/**
 * Ids selected together with `unitId`, including the unit itself.
 * `depth` is how many levels below the clicked unit are included (1 = direct children).
 * @param {Map<string, object>} byId
 * @param {string} unitId
 * @param {{ mode?: 'self' | 'all' | 'depth', depth?: number }} [cascade]
 * @returns {string[]}
 */
export function unitSelectionScope(byId, unitId, cascade = {}) {
    const id = String(unitId ?? '');
    const node = byId?.get?.(id);
    if (!node) return id ? [id] : [];
    const mode = cascade.mode || 'self';
    if (mode === 'all') return [node.id, ...node.descendantIds];
    if (mode !== 'depth') return [node.id];

    const levels = Math.max(1, Number(cascade.depth) || 1);
    const maxDepth = node.depth + levels;
    const ids = [node.id];
    const walk = (current) => {
        for (const child of linkedChildren(current)) {
            if (child.depth > maxDepth) continue;
            ids.push(child.id);
            walk(child);
        }
    };
    walk(node);
    return ids;
}

/**
 * @param {string[]} nextIds values after the click
 * @param {{ action?: string, option?: { kind?: string, value?: unknown } }} actionMeta
 * @param {Map<string, object>} byId
 * @param {{ mode?: string, depth?: number }} cascade
 * @returns {string[]}
 */
export function applyUnitCascadeToValues(nextIds, actionMeta, byId, cascade) {
    const ids = (Array.isArray(nextIds) ? nextIds : EMPTY).map((id) => String(id));
    const option = actionMeta?.option;
    if (!option || option.kind !== 'unit' || !cascade || cascade.mode === 'self') return ids;

    const scope = new Set(unitSelectionScope(byId, option.value, cascade));
    const action = actionMeta.action;
    if (action === 'select-option') {
        const seen = new Set(ids);
        const ordered = [...ids];
        for (const id of scope) {
            if (seen.has(id)) continue;
            seen.add(id);
            ordered.push(id);
        }
        return ordered;
    }
    if (action === 'deselect-option' || action === 'remove-value' || action === 'pop-value') {
        return ids.filter((id) => !scope.has(id));
    }
    return ids;
}
